import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Form, Modal, Badge, Spinner, Accordion, OverlayTrigger, Tooltip, Alert } from 'react-bootstrap';
import { 
  FaBuilding, 
  FaDoorOpen, 
  FaBed, 
  FaPlus, 
  FaEdit, 
  FaTrash, 
  FaChartBar,
  FaMapMarkerAlt,
  FaInfoCircle,
  FaExpand,
  FaCompress
} from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';
import * as infraApi from '../../../services/api/infraestructuraApi';
import proyectoApi from '../../../services/api/proyectoApi';
import 'react-toastify/dist/ReactToastify.css';
import './GestionInfraestructura.css';

const GestionInfraestructura = () => {
  const [loading, setLoading] = useState(false);
  const [proyectos, setProyectos] = useState([]);
  const [proyectoSeleccionado, setProyectoSeleccionado] = useState('');
  const [infraestructura, setInfraestructura] = useState([]);
  const [estadisticas, setEstadisticas] = useState(null);
  
  // Modales
  const [showModalZona, setShowModalZona] = useState(false);
  const [showModalModulo, setShowModalModulo] = useState(false);
  const [showModalHabitacion, setShowModalHabitacion] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  
  // Datos del formulario
  const [editingZona, setEditingZona] = useState(null);
  const [editingModulo, setEditingModulo] = useState(null);
  const [editingHabitacion, setEditingHabitacion] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  
  // Formularios
  const [formZona, setFormZona] = useState({
    nombre: '',
    descripcion: '',
    proyecto_id: ''
  });
  
  const [formModulo, setFormModulo] = useState({
    codigo: '',
    nombre: '',
    descripcion: '',
    zona_id: '',
    estado_modulo_id: 1,
    capacidad_total: '',
    observaciones: ''
  });
  
  const [formHabitacion, setFormHabitacion] = useState({
    numero: '',
    piso: 1,
    capacidad: '',
    modulo_id: '',
    estado_habitacion_id: 1,
    observaciones: ''
  });

  // Estado de expansión de cards
  const [expandedZonas, setExpandedZonas] = useState({});
  const [expandedModulos, setExpandedModulos] = useState({});

  useEffect(() => {
    cargarProyectos();
  }, []);

  useEffect(() => {
    if (proyectoSeleccionado) {
      cargarInfraestructura();
      cargarEstadisticas();
    }
  }, [proyectoSeleccionado]);

  const cargarProyectos = async () => {
    try {
      const data = await proyectoApi.getProyectos();
      setProyectos(data);
      if (data.length > 0) {
        setProyectoSeleccionado(data[0].id);
      }
    } catch (error) {
      toast.error('Error al cargar proyectos');
    }
  };

  const cargarInfraestructura = async () => {
    setLoading(true);
    try {
      const data = await infraApi.getInfraestructuraResumen(proyectoSeleccionado);
      setInfraestructura(data.data);
    } catch (error) {
      toast.error('Error al cargar infraestructura');
    } finally {
      setLoading(false);
    }
  };

  const cargarEstadisticas = async () => {
    try {
      const data = await infraApi.getInfraestructuraEstadisticas(proyectoSeleccionado);
      setEstadisticas(data.data);
    } catch (error) {
      console.error('Error al cargar estadísticas', error);
    }
  };

  // ============== ZONA ==============
  
  const handleShowModalZona = (zona = null) => {
    if (zona) {
      setEditingZona(zona);
      setFormZona({
        nombre: zona.nombre,
        descripcion: zona.descripcion || '',
        proyecto_id: zona.proyecto_id
      });
    } else {
      setEditingZona(null);
      setFormZona({
        nombre: '',
        descripcion: '',
        proyecto_id: proyectoSeleccionado
      });
    }
    setShowModalZona(true);
  };

  const handleCloseModalZona = () => {
    setShowModalZona(false);
    setEditingZona(null);
  };

  const handleSubmitZona = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingZona) {
        await infraApi.updateZona(editingZona.id, formZona);
        toast.success('Zona actualizada correctamente');
      } else {
        await infraApi.createZona(formZona);
        toast.success('Zona creada correctamente');
      }
      handleCloseModalZona();
      cargarInfraestructura();
      cargarEstadisticas();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar zona');
    } finally {
      setLoading(false);
    }
  };

  // ============== MÓDULO ==============
  
  const handleShowModalModulo = (modulo = null, zonaId = null) => {
    if (modulo) {
      setEditingModulo(modulo);
      setFormModulo({
        codigo: modulo.codigo,
        nombre: modulo.nombre,
        descripcion: modulo.descripcion || '',
        zona_id: modulo.zona_id,
        estado_modulo_id: modulo.estado_modulo_id,
        capacidad_total: modulo.capacidad_total,
        observaciones: modulo.observaciones || ''
      });
    } else {
      setEditingModulo(null);
      setFormModulo({
        codigo: '',
        nombre: '',
        descripcion: '',
        zona_id: zonaId || '',
        estado_modulo_id: 1,
        capacidad_total: '',
        observaciones: ''
      });
    }
    setShowModalModulo(true);
  };

  const handleCloseModalModulo = () => {
    setShowModalModulo(false);
    setEditingModulo(null);
  };

  const handleSubmitModulo = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = {
        ...formModulo,
        capacidad_total: parseInt(formModulo.capacidad_total)
      };
      
      if (editingModulo) {
        await infraApi.updateModulo(editingModulo.id, data);
        toast.success('Módulo actualizado correctamente');
      } else {
        await infraApi.createModulo(data);
        toast.success('Módulo creado correctamente');
      }
      handleCloseModalModulo();
      cargarInfraestructura();
      cargarEstadisticas();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar módulo');
    } finally {
      setLoading(false);
    }
  };

  // ============== HABITACIÓN ==============
  
  const handleShowModalHabitacion = (habitacion = null, moduloId = null) => {
    if (habitacion) {
      setEditingHabitacion(habitacion);
      setFormHabitacion({
        numero: habitacion.numero,
        piso: habitacion.piso,
        capacidad: habitacion.capacidad,
        modulo_id: habitacion.modulo_id,
        estado_habitacion_id: habitacion.estado_habitacion_id,
        observaciones: habitacion.observaciones || ''
      });
    } else {
      setEditingHabitacion(null);
      setFormHabitacion({
        numero: '',
        piso: 1,
        capacidad: '',
        modulo_id: moduloId || '',
        estado_habitacion_id: 1,
        observaciones: ''
      });
    }
    setShowModalHabitacion(true);
  };

  const handleCloseModalHabitacion = () => {
    setShowModalHabitacion(false);
    setEditingHabitacion(null);
  };

  const handleSubmitHabitacion = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = {
        ...formHabitacion,
        piso: parseInt(formHabitacion.piso),
        capacidad: parseInt(formHabitacion.capacidad)
      };
      
      if (editingHabitacion) {
        await infraApi.updateHabitacion(editingHabitacion.id, data);
        toast.success('Habitación actualizada correctamente');
      } else {
        await infraApi.createHabitacion(data);
        toast.success('Habitación creada correctamente');
      }
      handleCloseModalHabitacion();
      cargarInfraestructura();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar habitación');
    } finally {
      setLoading(false);
    }
  };

  // ============== ELIMINAR ==============
  
  const handleShowDeleteModal = (type, item) => {
    setDeleteTarget({ type, item });
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    
    setLoading(true);
    try {
      const { type, item } = deleteTarget;
      
      if (type === 'zona') {
        await infraApi.deleteZona(item.id);
        toast.success('Zona eliminada correctamente');
      } else if (type === 'modulo') {
        await infraApi.deleteModulo(item.id);
        toast.success('Módulo eliminado correctamente');
      } else if (type === 'habitacion') {
        await infraApi.deleteHabitacion(item.id);
        toast.success('Habitación eliminada correctamente');
      }
      
      setShowDeleteModal(false);
      setDeleteTarget(null);
      cargarInfraestructura();
      cargarEstadisticas();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    } finally {
      setLoading(false);
    }
  };

  // ============== EXPANSIÓN ==============
  
  const toggleZonaExpansion = (zonaId) => {
    setExpandedZonas(prev => ({
      ...prev,
      [zonaId]: !prev[zonaId]
    }));
  };

  const toggleModuloExpansion = (moduloId) => {
    setExpandedModulos(prev => ({
      ...prev,
      [moduloId]: !prev[moduloId]
    }));
  };

  return (
    <Container fluid className="gestion-infraestructura">
      <ToastContainer position="top-right" autoClose={3000} />
      
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2>
                <FaBuilding className="me-2" />
                Gestión de Infraestructura Hotelera
              </h2>
              <p className="text-muted">Vista interactiva de zonas, módulos y habitaciones</p>
            </div>
            <Button 
              variant="success" 
              onClick={() => handleShowModalZona()}
              disabled={!proyectoSeleccionado}
            >
              <FaPlus className="me-2" />
              Nueva Zona
            </Button>
          </div>
        </Col>
      </Row>

      {/* Selector de Proyecto y Estadísticas */}
      <Row className="mb-4">
        <Col md={4}>
          <Card>
            <Card.Body>
              <Form.Group>
                <Form.Label><strong>Proyecto</strong></Form.Label>
                <Form.Select
                  value={proyectoSeleccionado}
                  onChange={(e) => setProyectoSeleccionado(e.target.value)}
                >
                  <option value="">Seleccione un proyecto</option>
                  {proyectos.map(p => (
                    <option key={p.id} value={p.id}>{p.nombre}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Card.Body>
          </Card>
        </Col>
        
        {estadisticas && (
          <>
            <Col md={2}>
              <Card className="stat-card stat-zonas">
                <Card.Body className="text-center">
                  <FaMapMarkerAlt size={30} className="mb-2" />
                  <h3 className="mb-0">{estadisticas.total_zonas}</h3>
                  <small>Zonas</small>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="stat-card stat-modulos">
                <Card.Body className="text-center">
                  <FaDoorOpen size={30} className="mb-2" />
                  <h3 className="mb-0">{estadisticas.total_modulos}</h3>
                  <small>Módulos</small>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="stat-card stat-capacidad">
                <Card.Body className="text-center">
                  <FaBed size={30} className="mb-2" />
                  <h3 className="mb-0">{estadisticas.capacidad_total}</h3>
                  <small>Capacidad Total</small>
                </Card.Body>
              </Card>
            </Col>
          </>
        )}
      </Row>

      {/* Contenido Principal */}
      {loading && !infraestructura.length ? (
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Cargando...</span>
          </Spinner>
        </div>
      ) : (
        <Row>
          {infraestructura.length === 0 && proyectoSeleccionado ? (
            <Col>
              <Alert variant="info">
                <FaInfoCircle className="me-2" />
                No hay infraestructura configurada para este proyecto. Comienza creando una zona.
              </Alert>
            </Col>
          ) : (
            infraestructura.map(zona => (
              <Col key={zona.id} xs={12} className="mb-4">
                <Card className="zona-card">
                  <Card.Header className="zona-header">
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="d-flex align-items-center">
                        <Button
                          variant="link"
                          className="p-0 me-2"
                          onClick={() => toggleZonaExpansion(zona.id)}
                        >
                          {expandedZonas[zona.id] ? <FaCompress /> : <FaExpand />}
                        </Button>
                        <FaMapMarkerAlt className="me-2" size={20} />
                        <div>
                          <h5 className="mb-0">{zona.nombre}</h5>
                          {zona.descripcion && (
                            <small className="text-muted">{zona.descripcion}</small>
                          )}
                        </div>
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        <Badge bg="info" pill>
                          {zona.total_modulos || 0} módulos
                        </Badge>
                        <Badge bg="secondary" pill>
                          {zona.capacidad_total || 0} camas
                        </Badge>
                        <Button 
                          size="sm" 
                          variant="outline-primary"
                          onClick={() => handleShowModalModulo(null, zona.id)}
                        >
                          <FaPlus className="me-1" /> Módulo
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline-warning"
                          onClick={() => handleShowModalZona(zona)}
                        >
                          <FaEdit />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline-danger"
                          onClick={() => handleShowDeleteModal('zona', zona)}
                        >
                          <FaTrash />
                        </Button>
                      </div>
                    </div>
                  </Card.Header>
                  
                  {expandedZonas[zona.id] !== false && zona.modulos && zona.modulos.length > 0 && (
                    <Card.Body>
                      <Row>
                        {zona.modulos.map(modulo => (
                          <Col key={modulo.id} md={6} lg={4} className="mb-3">
                            <Card className="modulo-card h-100">
                              <Card.Header className="modulo-header">
                                <div className="d-flex justify-content-between align-items-start">
                                  <div className="flex-grow-1">
                                    <div className="d-flex align-items-center mb-1">
                                      <FaDoorOpen className="me-2" />
                                      <strong>{modulo.codigo}</strong>
                                    </div>
                                    <div className="text-muted small">{modulo.nombre}</div>
                                  </div>
                                  <div className="d-flex gap-1">
                                    <Button 
                                      size="sm" 
                                      variant="link"
                                      onClick={() => toggleModuloExpansion(modulo.id)}
                                      className="p-0"
                                    >
                                      {expandedModulos[modulo.id] ? <FaCompress /> : <FaExpand />}
                                    </Button>
                                  </div>
                                </div>
                              </Card.Header>
                              <Card.Body className="modulo-body">
                                <div className="d-flex justify-content-between mb-2">
                                  <small>
                                    <FaBed className="me-1" />
                                    Capacidad: <strong>{modulo.capacidad_total}</strong>
                                  </small>
                                  <small>
                                    Habitaciones: <strong>{modulo.total_habitaciones || 0}</strong>
                                  </small>
                                </div>
                                
                                {expandedModulos[modulo.id] !== false && modulo.habitaciones && modulo.habitaciones.length > 0 && (
                                  <div className="habitaciones-list mt-3">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                      <small className="text-muted"><strong>Habitaciones:</strong></small>
                                      <Button 
                                        size="sm" 
                                        variant="outline-primary"
                                        onClick={() => handleShowModalHabitacion(null, modulo.id)}
                                      >
                                        <FaPlus />
                                      </Button>
                                    </div>
                                    {modulo.habitaciones.map(habitacion => (
                                      <div key={habitacion.id} className="habitacion-item">
                                        <div className="d-flex justify-content-between align-items-center">
                                          <div>
                                            <FaBed className="me-2" />
                                            <strong>Hab. {habitacion.numero}</strong>
                                            <span className="ms-2 text-muted">
                                              Piso {habitacion.piso}
                                            </span>
                                            <Badge bg="secondary" pill className="ms-2">
                                              {habitacion.capacidad} camas
                                            </Badge>
                                          </div>
                                          <div>
                                            <Button 
                                              size="sm" 
                                              variant="link"
                                              onClick={() => handleShowModalHabitacion(habitacion)}
                                              className="p-0 me-2"
                                            >
                                              <FaEdit />
                                            </Button>
                                            <Button 
                                              size="sm" 
                                              variant="link"
                                              onClick={() => handleShowDeleteModal('habitacion', habitacion)}
                                              className="p-0 text-danger"
                                            >
                                              <FaTrash />
                                            </Button>
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                                
                                {(!modulo.habitaciones || modulo.habitaciones.length === 0) && (
                                  <div className="text-center mt-3">
                                    <small className="text-muted">No hay habitaciones</small>
                                    <br />
                                    <Button 
                                      size="sm" 
                                      variant="outline-primary"
                                      onClick={() => handleShowModalHabitacion(null, modulo.id)}
                                      className="mt-2"
                                    >
                                      <FaPlus className="me-1" /> Agregar Habitación
                                    </Button>
                                  </div>
                                )}
                              </Card.Body>
                              <Card.Footer className="modulo-footer">
                                <div className="d-flex justify-content-end gap-2">
                                  <Button 
                                    size="sm" 
                                    variant="outline-warning"
                                    onClick={() => handleShowModalModulo(modulo)}
                                  >
                                    <FaEdit /> Editar
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    variant="outline-danger"
                                    onClick={() => handleShowDeleteModal('modulo', modulo)}
                                  >
                                    <FaTrash /> Eliminar
                                  </Button>
                                </div>
                              </Card.Footer>
                            </Card>
                          </Col>
                        ))}
                      </Row>
                    </Card.Body>
                  )}
                  
                  {expandedZonas[zona.id] !== false && (!zona.modulos || zona.modulos.length === 0) && (
                    <Card.Body>
                      <Alert variant="light" className="text-center">
                        <FaInfoCircle className="me-2" />
                        No hay módulos en esta zona.
                        <Button 
                          size="sm" 
                          variant="primary"
                          onClick={() => handleShowModalModulo(null, zona.id)}
                          className="ms-3"
                        >
                          <FaPlus className="me-1" /> Crear Módulo
                        </Button>
                      </Alert>
                    </Card.Body>
                  )}
                </Card>
              </Col>
            ))
          )}
        </Row>
      )}

      {/* Modal Zona */}
      <Modal show={showModalZona} onHide={handleCloseModalZona} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingZona ? 'Editar Zona' : 'Nueva Zona'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmitZona}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Nombre <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    value={formZona.nombre}
                    onChange={(e) => setFormZona({ ...formZona, nombre: e.target.value })}
                    required
                    maxLength={50}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Proyecto <span className="text-danger">*</span></Form.Label>
                  <Form.Select
                    value={formZona.proyecto_id}
                    onChange={(e) => setFormZona({ ...formZona, proyecto_id: e.target.value })}
                    required
                    disabled={editingZona}
                  >
                    <option value="">Seleccione...</option>
                    {proyectos.map(p => (
                      <option key={p.id} value={p.id}>{p.nombre}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Descripción</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={formZona.descripcion}
                    onChange={(e) => setFormZona({ ...formZona, descripcion: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModalZona}>
              Cancelar
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? <Spinner size="sm" /> : (editingZona ? 'Actualizar' : 'Crear')}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modal Módulo */}
      <Modal show={showModalModulo} onHide={handleCloseModalModulo} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingModulo ? 'Editar Módulo' : 'Nuevo Módulo'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmitModulo}>
          <Modal.Body>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Código <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    value={formModulo.codigo}
                    onChange={(e) => setFormModulo({ ...formModulo, codigo: e.target.value })}
                    required
                    maxLength={20}
                  />
                </Form.Group>
              </Col>
              <Col md={8}>
                <Form.Group className="mb-3">
                  <Form.Label>Nombre <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    value={formModulo.nombre}
                    onChange={(e) => setFormModulo({ ...formModulo, nombre: e.target.value })}
                    required
                    maxLength={100}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Zona <span className="text-danger">*</span></Form.Label>
                  <Form.Select
                    value={formModulo.zona_id}
                    onChange={(e) => setFormModulo({ ...formModulo, zona_id: e.target.value })}
                    required
                    disabled={editingModulo}
                  >
                    <option value="">Seleccione...</option>
                    {infraestructura.map(z => (
                      <option key={z.id} value={z.id}>{z.nombre}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Capacidad Total <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="number"
                    value={formModulo.capacidad_total}
                    onChange={(e) => setFormModulo({ ...formModulo, capacidad_total: e.target.value })}
                    required
                    min="1"
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Descripción</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={formModulo.descripcion}
                    onChange={(e) => setFormModulo({ ...formModulo, descripcion: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Observaciones</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={formModulo.observaciones}
                    onChange={(e) => setFormModulo({ ...formModulo, observaciones: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModalModulo}>
              Cancelar
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? <Spinner size="sm" /> : (editingModulo ? 'Actualizar' : 'Crear')}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modal Habitación */}
      <Modal show={showModalHabitacion} onHide={handleCloseModalHabitacion} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingHabitacion ? 'Editar Habitación' : 'Nueva Habitación'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmitHabitacion}>
          <Modal.Body>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Número <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    value={formHabitacion.numero}
                    onChange={(e) => setFormHabitacion({ ...formHabitacion, numero: e.target.value })}
                    required
                    maxLength={10}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Piso <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="number"
                    value={formHabitacion.piso}
                    onChange={(e) => setFormHabitacion({ ...formHabitacion, piso: e.target.value })}
                    required
                    min="1"
                    max="20"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Capacidad <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="number"
                    value={formHabitacion.capacidad}
                    onChange={(e) => setFormHabitacion({ ...formHabitacion, capacidad: e.target.value })}
                    required
                    min="1"
                    max="8"
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Módulo <span className="text-danger">*</span></Form.Label>
                  <Form.Select
                    value={formHabitacion.modulo_id}
                    onChange={(e) => setFormHabitacion({ ...formHabitacion, modulo_id: e.target.value })}
                    required
                    disabled={editingHabitacion}
                  >
                    <option value="">Seleccione...</option>
                    {infraestructura.flatMap(z => 
                      z.modulos.map(m => (
                        <option key={m.id} value={m.id}>{z.nombre} - {m.codigo} {m.nombre}</option>
                      ))
                    )}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Observaciones</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={formHabitacion.observaciones}
                    onChange={(e) => setFormHabitacion({ ...formHabitacion, observaciones: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModalHabitacion}>
              Cancelar
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? <Spinner size="sm" /> : (editingHabitacion ? 'Actualizar' : 'Crear')}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modal Confirmar Eliminación */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirmar Eliminación</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {deleteTarget && (
            <p>
              ¿Está seguro de eliminar {deleteTarget.type === 'zona' ? 'la zona' : deleteTarget.type === 'modulo' ? 'el módulo' : 'la habitación'}{' '}
              <strong>
                {deleteTarget.type === 'zona' && deleteTarget.item.nombre}
                {deleteTarget.type === 'modulo' && `${deleteTarget.item.codigo} - ${deleteTarget.item.nombre}`}
                {deleteTarget.type === 'habitacion' && `Habitación ${deleteTarget.item.numero}`}
              </strong>?
            </p>
          )}
          <Alert variant="warning">
            <FaInfoCircle className="me-2" />
            Esta acción no se puede deshacer.
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancelar
          </Button>
          <Button variant="danger" onClick={handleConfirmDelete} disabled={loading}>
            {loading ? <Spinner size="sm" /> : 'Eliminar'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default GestionInfraestructura;
